// Platform reference data contracts — ICD-10 codes, SAOA tariff codes, medical-aid schemes / plans / options and the
// patient-card condition list (modules-and-api.md §5.1, §5.9, §5.10; decision D30; UC-PLATFORM-REFERENCE-001..007).
//
// One copy in the platform database, written by a Platform Admin through the collection routes under /platform/reference
// and read by every tenant under /reference/* and /icd10-codes/*. Rows are effective-dated and never deleted: a row leaves
// service by a discontinued date (BR-PLATFORM-REFERENCE-002). Every reference view carries the shared window
// (`effectiveFrom`, `effectiveTo`, `status`, `discontinuedDate`, `inEffect`) and a `version` for If-Match (§0.3).
import { z } from "zod";
import { InstantSchema, LocalDateSchema, UlidSchema } from "./primitives";

export const REFERENCE_STATUSES = ["Active", "Discontinued"] as const;
export type ReferenceStatus = (typeof REFERENCE_STATUSES)[number];
export const SAOA_CODE_TYPES = ["Lens", "Coating", "Colour", "Consultation", "Other"] as const;
export type SaoaCodeType = (typeof SAOA_CODE_TYPES)[number];
export const SCHEME_SOURCES = ["manual", "medprax"] as const;
/**
 * How a load treats what is already stored (BR-PLATFORM-REFERENCE-006, 011):
 * - `partial`: adds and changes, never discontinues (an amendment, a CSV of a few rows);
 * - `complete`: the file is the whole set — active codes absent from it are discontinued (the MIT, the annual SAOA schedule);
 * - `seed`: adds what is missing and changes nothing that exists (the fixture seed; safe before or after an official load).
 */
export const REFERENCE_LOAD_MODES = ["partial", "complete", "seed"] as const;
export type ReferenceLoadMode = (typeof REFERENCE_LOAD_MODES)[number];
/** Medical-aid rows and conditions are curated by hand, so a file never discontinues them; only PATCH does. */
export const CURATED_LOAD_MODES = ["partial", "seed"] as const;
export const REFERENCE_LOAD_TRIGGERS = ["manual", "seed"] as const;
/** The largest file one load accepts. The 2021 MIT has 33 961 rows, so a full table is sent as partial chunks by category and then finalised (apps/api/scripts/load-icd10.py). */
export const REFERENCE_MAX_ROWS = 20_000;
/**
 * The plausibility guard on discontinuation (UC-PLATFORM-REFERENCE-001 E4, D48): a complete load or a finalise step that
 * would discontinue MORE than this many codes AND more than this share (basis points) of the stored Active codes is refused
 * with 422 until the request repeats the count in `confirmDiscontinue`. 20 codes and 5 %: an annual MIT retires a few dozen
 * codes; a chunk sent as `complete` by mistake would retire thousands.
 */
export const ICD10_DISCONTINUE_GUARD_ROWS = 20;
export const ICD10_DISCONTINUE_GUARD_BP = 500;
/** Tenant lookups are bounded pages (BR-PLATFORM-REFERENCE-009). */
export const REFERENCE_PAGE_DEFAULT = 50;
export const REFERENCE_PAGE_MAX = 200;
/** The ICD-10 typeahead needs at least this many characters; a shorter term is an empty page without a query. */
export const ICD10_MIN_TERM = 2;

// ---------------------------------------------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------------------------------------------

/** MIT dotted form: a letter, two digits, an optional dotted one- or two-digit subcode (`H52.1`, `Z01.0`, `H52`). */
/** A WHO code: a letter, two digits, an optional sub-code whose first character may be the `X` placeholder the SA MIT carries (`M45.X0`, `V98.X0`). */
export const Icd10CodeSchema = z.string().trim().toUpperCase().regex(/^[A-Z]\d{2}(\.[0-9X]\d?)?$/, "ICD-10 code such as H52.1 or M45.X0");
/** SAOA codes are short numeric strings (`1001`, `2001`); kept as TEXT and matched verbatim. */
export const SaoaCodeSchema = z.string().trim().regex(/^[A-Za-z0-9]{2,12}$/, "SAOA code such as 1001");
/** A stable machine key for a condition, e.g. `OPEN_ANGLE_GLAUCOMA`. Never changes once seeded. */
export const ConditionKeySchema = z.string().regex(/^[A-Z][A-Z0-9_]{1,63}$/, "an upper-case key such as OPEN_ANGLE_GLAUCOMA");
const Text = (max: number) => z.string().trim().min(1).max(max);
const NullableText = (max: number) => z.string().trim().max(max).nullable();
const Bool = z.enum(["true", "false"]);

/** The shared window and stamps of every reference view. */
export const ReferenceWindowViewShape = {
  effectiveFrom: LocalDateSchema,
  effectiveTo: LocalDateSchema.nullable(),
  status: z.enum(REFERENCE_STATUSES),
  /** `effectiveTo` when the row was discontinued; null while active or merely expired. */
  discontinuedDate: LocalDateSchema.nullable(),
  /** Whether the row is Active and its window covers the request date (or `asAt` when given). */
  inEffect: z.boolean(),
  /** Optimistic-concurrency version (the row's `updated_at` as epoch milliseconds); sent back as `If-Match: W/"<version>"`. */
  version: z.number().int(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
};

/** Optional window fields on an input row; a row without `effectiveFrom` is in effect from the day it is loaded. */
const WindowInputShape = {
  effectiveFrom: LocalDateSchema.optional(),
  effectiveTo: LocalDateSchema.optional(),
};

/** `discontinuedDate` on a PATCH: a date discontinues the row on that date; `null` returns it to service. */
const DiscontinuePatchShape = {
  /** The version the client read (`If-Match` is equivalent; both present must agree). */
  version: z.number().int().optional(),
  discontinuedDate: LocalDateSchema.nullable().optional(),
};

// ---------------------------------------------------------------------------------------------------------------
// Loads and their result
// ---------------------------------------------------------------------------------------------------------------

export const ReferenceLoadResultSchema = z.object({
  /** Identifies the refresh run in the platform log (BR-PLATFORM-REFERENCE-008). */
  runId: UlidSchema,
  trigger: z.enum(REFERENCE_LOAD_TRIGGERS),
  mode: z.enum(REFERENCE_LOAD_MODES),
  sourceVersion: z.string().nullable(),
  added: z.number().int(),
  changed: z.number().int(),
  discontinued: z.number().int(),
  unchanged: z.number().int(),
});
export type ReferenceLoadResult = z.infer<typeof ReferenceLoadResultSchema>;
/** Loads of ULID-keyed rows also return the stored rows so a caller learns the ids it was given. */
export const LoadResultWithItems = <S extends z.ZodType>(item: S) => ReferenceLoadResultSchema.extend({ items: z.array(item) });

const rows = <S extends z.ZodType>(row: S) => z.array(row).min(1).max(REFERENCE_MAX_ROWS);

// ---------------------------------------------------------------------------------------------------------------
// ICD-10
// ---------------------------------------------------------------------------------------------------------------

export const Icd10CodeInputSchema = z.strictObject({
  code: Icd10CodeSchema,
  description: Text(500),
  longDescription: NullableText(2000).optional(),
  /** e.g. `VII` — Diseases of the eye and adnexa. */
  chapter: NullableText(16).optional(),
  /** MIT block, e.g. `H49-H52`. */
  block: NullableText(16).optional(),
  isValidAsPrimary: z.boolean().optional(),
  isPmb: z.boolean().optional(),
  isAsterisk: z.boolean().optional(),
  ...WindowInputShape,
});
export type Icd10CodeInput = z.infer<typeof Icd10CodeInputSchema>;

export const Icd10LoadRequestSchema = z.strictObject({
  mode: z.enum(REFERENCE_LOAD_MODES).default("partial"),
  /** The release label, e.g. `MIT 2026`; stamped on every row the load adds or changes. */
  sourceVersion: Text(64),
  /**
   * The release's date (D48): releases are ordered by it, never by a row's own dates — MIT 2021 is `2021-03-16`, the date in
   * the workbook's name; the seed carries `2016-01-01` so every official release supersedes it. Stored on every row the
   * release lists and on the refresh run.
   */
  releaseDate: LocalDateSchema,
  /** A `complete` load that would discontinue more than the plausibility guard allows must repeat that count here (UC-PLATFORM-REFERENCE-001 E4). */
  confirmDiscontinue: z.number().int().min(0).optional(),
  rows: rows(Icd10CodeInputSchema),
});
export type Icd10LoadRequest = z.infer<typeof Icd10LoadRequestSchema>;

/**
 * `POST /platform/reference/icd10-codes/finalise` — closes a release loaded in partial chunks: every Active code whose release
 * is older than `releaseDate` (or has none) was listed by no chunk and is discontinued from that date, under the same guard.
 */
export const Icd10FinaliseRequestSchema = z.strictObject({
  sourceVersion: Text(64),
  releaseDate: LocalDateSchema,
  confirmDiscontinue: z.number().int().min(0).optional(),
});
export type Icd10FinaliseRequest = z.infer<typeof Icd10FinaliseRequestSchema>;

export const Icd10FinaliseResultSchema = z.object({
  runId: UlidSchema,
  sourceVersion: z.string(),
  releaseDate: LocalDateSchema,
  /** Codes discontinued by this step. */
  discontinued: z.number().int(),
});
export type Icd10FinaliseResult = z.infer<typeof Icd10FinaliseResultSchema>;

export const Icd10CodeViewSchema = z.object({
  code: z.string(),
  description: z.string(),
  longDescription: z.string().nullable(),
  chapter: z.string().nullable(),
  block: z.string().nullable(),
  isValidAsPrimary: z.boolean(),
  isPmb: z.boolean(),
  isAsterisk: z.boolean(),
  sourceVersion: z.string().nullable(),
  /** The date of the release that last listed the code (D48); null on rows loaded before releases were dated. */
  releaseDate: LocalDateSchema.nullable(),
  ...ReferenceWindowViewShape,
});
export type Icd10CodeView = z.infer<typeof Icd10CodeViewSchema>;

export const Icd10PatchSchema = z.strictObject({
  code: Icd10CodeSchema,
  description: Text(500).optional(),
  longDescription: NullableText(2000).optional(),
  chapter: NullableText(16).optional(),
  block: NullableText(16).optional(),
  isValidAsPrimary: z.boolean().optional(),
  isPmb: z.boolean().optional(),
  isAsterisk: z.boolean().optional(),
  effectiveFrom: LocalDateSchema.optional(),
  ...DiscontinuePatchShape,
});
export type Icd10Patch = z.infer<typeof Icd10PatchSchema>;

/** `GET /icd10-codes/search` and the platform list. `q` matches the start of the code or the start of a description word. */
export const Icd10SearchQuerySchema = z.object({
  q: z.string().trim().max(64).optional(),
  limit: z.coerce.number().int().min(1).max(REFERENCE_PAGE_MAX).default(REFERENCE_PAGE_DEFAULT),
  cursor: z.string().max(512).optional(),
  includeDiscontinued: Bool.optional(),
  /** Rows in effect on this date instead of today (a 2026 document resolving a code the 2027 file closed). */
  asAt: LocalDateSchema.optional(),
});
export type Icd10SearchQuery = z.infer<typeof Icd10SearchQuerySchema>;

export const ReferenceItemQuerySchema = z.object({ asAt: LocalDateSchema.optional() });

// ---------------------------------------------------------------------------------------------------------------
// SAOA
// ---------------------------------------------------------------------------------------------------------------

/** A schedule row carries a description and a type — never a price (BR-PLATFORM-REFERENCE-005); a price column is an unknown key. */
export const SaoaCodeInputSchema = z.strictObject({
  code: SaoaCodeSchema,
  description: Text(500),
  codeType: z.enum(SAOA_CODE_TYPES),
  ...WindowInputShape,
});
export type SaoaCodeInput = z.infer<typeof SaoaCodeInputSchema>;

export const SaoaLoadRequestSchema = z.strictObject({
  mode: z.enum(REFERENCE_LOAD_MODES).default("partial"),
  /** The schedule label, e.g. `SAOA 2026`. */
  sourceVersion: Text(64),
  rows: rows(SaoaCodeInputSchema),
});
export type SaoaLoadRequest = z.infer<typeof SaoaLoadRequestSchema>;

export const SaoaCodeViewSchema = z.object({
  code: z.string(),
  description: z.string(),
  codeType: z.enum(SAOA_CODE_TYPES),
  scheduleVersion: z.string().nullable(),
  ...ReferenceWindowViewShape,
});
export type SaoaCodeView = z.infer<typeof SaoaCodeViewSchema>;

export const SaoaPatchSchema = z.strictObject({
  code: SaoaCodeSchema,
  description: Text(500).optional(),
  codeType: z.enum(SAOA_CODE_TYPES).optional(),
  effectiveFrom: LocalDateSchema.optional(),
  effectiveTo: LocalDateSchema.nullable().optional(),
  ...DiscontinuePatchShape,
});
export type SaoaPatch = z.infer<typeof SaoaPatchSchema>;

export const SaoaListQuerySchema = z.object({
  /** Code prefix or the start of a description word. */
  q: z.string().trim().max(64).optional(),
  type: z.enum(SAOA_CODE_TYPES).optional(),
  limit: z.coerce.number().int().min(1).max(REFERENCE_PAGE_MAX).default(REFERENCE_PAGE_DEFAULT),
  cursor: z.string().max(512).optional(),
  includeDiscontinued: Bool.optional(),
  asAt: LocalDateSchema.optional(),
});
export type SaoaListQuery = z.infer<typeof SaoaListQuerySchema>;

// ---------------------------------------------------------------------------------------------------------------
// Medical-aid schemes, plans and options
// ---------------------------------------------------------------------------------------------------------------

const SchemeFields = {
  name: Text(200),
  /** PAT-85 scheme code / MediSwitch destination code; unique where present. */
  schemeCode: NullableText(32).optional(),
  /** CMS registration number; required on every scheme (UC-PLATFORM-REFERENCE-003 E2). */
  registrationNumber: Text(32),
  contactName: NullableText(200).optional(),
  phone: NullableText(40).optional(),
  email: z.string().trim().toLowerCase().email().max(254).nullable().optional(),
  hbRoutingCode: NullableText(32).optional(),
  mediswitchDestinationCode: NullableText(32).optional(),
  medpraxSchemeId: NullableText(64).optional(),
  source: z.enum(SCHEME_SOURCES).default("manual"),
};

export const MedicalAidSchemeInputSchema = z.strictObject({
  /** Supply a ULID to upsert a known row (the seed does); omit to match on `schemeCode`, then name, then insert. */
  id: UlidSchema.optional(),
  ...SchemeFields,
  ...WindowInputShape,
});
export type MedicalAidSchemeInput = z.infer<typeof MedicalAidSchemeInputSchema>;

export const MedicalAidSchemeViewSchema = z.object({
  id: UlidSchema,
  name: z.string(),
  schemeCode: z.string().nullable(),
  registrationNumber: z.string().nullable(),
  contactName: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  hbRoutingCode: z.string().nullable(),
  mediswitchDestinationCode: z.string().nullable(),
  medpraxSchemeId: z.string().nullable(),
  source: z.enum(SCHEME_SOURCES),
  isVerified: z.boolean(),
  ...ReferenceWindowViewShape,
});
export type MedicalAidSchemeView = z.infer<typeof MedicalAidSchemeViewSchema>;

export const MedicalAidSchemePatchSchema = z.strictObject({
  id: UlidSchema,
  name: Text(200).optional(),
  schemeCode: NullableText(32).optional(),
  registrationNumber: Text(32).optional(),
  contactName: NullableText(200).optional(),
  phone: NullableText(40).optional(),
  email: z.string().trim().toLowerCase().email().max(254).nullable().optional(),
  hbRoutingCode: NullableText(32).optional(),
  mediswitchDestinationCode: NullableText(32).optional(),
  medpraxSchemeId: NullableText(64).optional(),
  effectiveFrom: LocalDateSchema.optional(),
  ...DiscontinuePatchShape,
});
export type MedicalAidSchemePatch = z.infer<typeof MedicalAidSchemePatchSchema>;

export const MedicalAidPlanInputSchema = z.strictObject({
  id: UlidSchema.optional(),
  schemeId: UlidSchema,
  name: Text(200),
  /** Unique within the scheme where present (INT-46 import key). */
  planCode: NullableText(32).optional(),
  medpraxPlanId: NullableText(64).optional(),
  ...WindowInputShape,
});
export type MedicalAidPlanInput = z.infer<typeof MedicalAidPlanInputSchema>;

export const MedicalAidPlanViewSchema = z.object({
  id: UlidSchema,
  schemeId: UlidSchema,
  name: z.string(),
  planCode: z.string().nullable(),
  medpraxPlanId: z.string().nullable(),
  ...ReferenceWindowViewShape,
});
export type MedicalAidPlanView = z.infer<typeof MedicalAidPlanViewSchema>;

export const MedicalAidPlanPatchSchema = z.strictObject({
  id: UlidSchema,
  name: Text(200).optional(),
  planCode: NullableText(32).optional(),
  medpraxPlanId: NullableText(64).optional(),
  effectiveFrom: LocalDateSchema.optional(),
  ...DiscontinuePatchShape,
});
export type MedicalAidPlanPatch = z.infer<typeof MedicalAidPlanPatchSchema>;

export const MedicalAidOptionInputSchema = z.strictObject({
  id: UlidSchema.optional(),
  planId: UlidSchema,
  /** Optional cross-check: when given, the plan must belong to this scheme (UC-PLATFORM-REFERENCE-003 E2). */
  schemeId: UlidSchema.optional(),
  name: Text(200),
  optionCode: NullableText(32).optional(),
  hbRoutingCode: NullableText(32).optional(),
  medpraxOptionId: NullableText(64).optional(),
  ...WindowInputShape,
});
export type MedicalAidOptionInput = z.infer<typeof MedicalAidOptionInputSchema>;

export const MedicalAidOptionViewSchema = z.object({
  id: UlidSchema,
  planId: UlidSchema,
  schemeId: UlidSchema,
  name: z.string(),
  optionCode: z.string().nullable(),
  hbRoutingCode: z.string().nullable(),
  medpraxOptionId: z.string().nullable(),
  ...ReferenceWindowViewShape,
});
export type MedicalAidOptionView = z.infer<typeof MedicalAidOptionViewSchema>;

export const MedicalAidOptionPatchSchema = z.strictObject({
  id: UlidSchema,
  name: Text(200).optional(),
  optionCode: NullableText(32).optional(),
  hbRoutingCode: NullableText(32).optional(),
  medpraxOptionId: NullableText(64).optional(),
  effectiveFrom: LocalDateSchema.optional(),
  ...DiscontinuePatchShape,
});
export type MedicalAidOptionPatch = z.infer<typeof MedicalAidOptionPatchSchema>;

const curatedLoad = <S extends z.ZodType>(row: S) => z.strictObject({
  mode: z.enum(CURATED_LOAD_MODES).default("partial"),
  sourceVersion: Text(64).optional(),
  rows: rows(row),
});
export const MedicalAidSchemeLoadRequestSchema = curatedLoad(MedicalAidSchemeInputSchema);
export const MedicalAidPlanLoadRequestSchema = curatedLoad(MedicalAidPlanInputSchema);
export const MedicalAidOptionLoadRequestSchema = curatedLoad(MedicalAidOptionInputSchema);
export type MedicalAidSchemeLoadRequest = z.infer<typeof MedicalAidSchemeLoadRequestSchema>;
export type MedicalAidPlanLoadRequest = z.infer<typeof MedicalAidPlanLoadRequestSchema>;
export type MedicalAidOptionLoadRequest = z.infer<typeof MedicalAidOptionLoadRequestSchema>;

const PagedRefQuery = {
  limit: z.coerce.number().int().min(1).max(REFERENCE_PAGE_MAX).default(REFERENCE_PAGE_DEFAULT),
  cursor: z.string().max(512).optional(),
  includeDiscontinued: Bool.optional(),
  asAt: LocalDateSchema.optional(),
};
export const SchemesQuerySchema = z.object({ ...PagedRefQuery });
export const PlansQuerySchema = z.object({ schemeId: UlidSchema, ...PagedRefQuery });
export const OptionsQuerySchema = z.object({ planId: UlidSchema, ...PagedRefQuery });
/** The platform lists may span every scheme or plan. */
export const PlatformPlansQuerySchema = z.object({ schemeId: UlidSchema.optional(), ...PagedRefQuery });
export const PlatformOptionsQuerySchema = z.object({ planId: UlidSchema.optional(), ...PagedRefQuery });

// ---------------------------------------------------------------------------------------------------------------
// Patient-card conditions
// ---------------------------------------------------------------------------------------------------------------

export const PatientCardConditionInputSchema = z.strictObject({
  conditionKey: ConditionKeySchema,
  label: Text(200),
  /** Must exist in the ICD-10 table (BR-PLATFORM-REFERENCE-010). */
  icd10Code: Icd10CodeSchema,
  sortOrder: z.number().int().min(0).max(10_000),
  ...WindowInputShape,
});
export type PatientCardConditionInput = z.infer<typeof PatientCardConditionInputSchema>;

export const PatientCardConditionLoadRequestSchema = curatedLoad(PatientCardConditionInputSchema);
export type PatientCardConditionLoadRequest = z.infer<typeof PatientCardConditionLoadRequestSchema>;

export const PatientCardConditionViewSchema = z.object({
  conditionKey: z.string(),
  label: z.string(),
  icd10Code: z.string().nullable(),
  /** The mapped code's current description, resolved at read time (BR-PLATFORM-REFERENCE-007); null when unmapped or unknown. */
  icd10Description: z.string().nullable(),
  sortOrder: z.number().int(),
  active: z.boolean(),
  /** Set when a later ICD-10 load discontinued the mapped code (UC-PLATFORM-REFERENCE-005 A4). */
  mappedCodeDiscontinued: z.boolean(),
  ...ReferenceWindowViewShape,
});
export type PatientCardConditionView = z.infer<typeof PatientCardConditionViewSchema>;

export const PatientCardConditionPatchSchema = z.strictObject({
  conditionKey: ConditionKeySchema,
  label: Text(200).optional(),
  icd10Code: Icd10CodeSchema.optional(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
  /** `false` deactivates the condition today; `true` returns it to service. Equivalent to `discontinuedDate`. */
  active: z.boolean().optional(),
  ...DiscontinuePatchShape,
});
export type PatientCardConditionPatch = z.infer<typeof PatientCardConditionPatchSchema>;

export const ConditionsQuerySchema = z.object({ includeInactive: Bool.optional(), asAt: LocalDateSchema.optional() });
